// E8RouteTimeProofOpenTask.js
// Zadanie otwarte E8 (2026 zad. 16) - predkosc, droga, czas i uzasadnienie.

import React from "react";
import { BlockMath, InlineMath } from "../../../components/math/KatexWrappers";
import EquationInput from "../../../components/inputs/EquationInput";
import FractionInput from "../../../components/inputs/FractionInput";
import { scoreStaged } from "../../openTaskScoring";

// wyciaga surowy tekst z wartosci inputu (string lub {raw, parsed})
function rawStr(v) {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v.raw === "string") return v.raw;
  if (typeof v.value === "string") return v.value;
  return "";
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function buildData({ firstDistance, firstMinutes, totalDistance }) {
  const restDistance = totalDistance - firstDistance;
  const speed = firstDistance / (firstMinutes / 60);
  const restMinutes = Math.round((restDistance / speed) * 6000) / 100;
  return { firstDistance, firstMinutes, totalDistance, restDistance, speed, restMinutes, maxPoints: 3 };
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x || 1;
}

function fractionLatex(num, den) {
  const d = gcd(num, den);
  const n = num / d;
  const q = den / d;
  if (q === 1) return String(n);
  return "\\frac{" + n + "}{" + q + "}";
}

function minutesToHoursLatex(minutes) {
  return fractionLatex(minutes, 60);
}

function numberLatex(value) {
  const n = Number(value);
  return Number.isInteger(n) ? String(n) : String(value).replace(".", "{,}");
}

function lastNumberLatex(raw) {
  const nums = extractNumbers(raw);
  const value = nums.length ? nums[nums.length - 1] : null;
  return Number.isFinite(value) ? numberLatex(value) : null;
}

function createOriginalTask() {
  return {
    type: "E8RouteTimeProofOpenTask",
    data: buildData({ firstDistance: 48, firstMinutes: 40, totalDistance: 123 }),
    source: { variant: "original", exam: "E8", session: "2026_MAJ", taskNumber: 16 },
    tags: ["e8", "obliczenia-praktyczne", "predkosc-droga-czas", "zadanie-otwarte"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  const speed = pick([60, 72, 80, 90]);
  const firstMinutes = pick([30, 40, 45]);
  const firstDistance = (speed * firstMinutes) / 60;
  const restDistance = speed + pick([3, 5, 6, 8, 10, 12]);
  return {
    type: "E8RouteTimeProofOpenTask",
    data: buildData({ firstDistance, firstMinutes, totalDistance: firstDistance + restDistance }),
    source: { variant: "random", basedOn: "E8_2026_MAJ_16" },
    tags: ["e8", "obliczenia-praktyczne", "predkosc-droga-czas", "zadanie-otwarte"],
  };
}

function normalize(text) {
  return rawStr(text).toLowerCase().replace(/,/g, ".").replace(/\s+/g, "");
}

function extractNumbers(text) {
  return Array.from(normalize(text).matchAll(/-?\d+(?:\.\d+)?/g), (m) => Number(m[0])).filter(
    Number.isFinite
  );
}

function hasNum(value, target, tol = 0.05) {
  return extractNumbers(value).some((n) => Math.abs(n - target) <= tol);
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Z Polanki do Dębiny prowadzi jedna droga przez Jodłowo i ma długość <InlineMath math={`${data.totalDistance}\\ \\ \\text{km}`} />. Droga z Polanki do Jodłowa ma długość <InlineMath math={`${data.firstDistance}\\ \\ \\text{km}`} /> i samochód przejechał ją w czasie <InlineMath math={`${data.firstMinutes}\\ \\ \\text{min}`} />. Drogę z Jodłowa do Dębiny ten samochód pokonał z taką samą prędkością, jak drogę z Polanki do Jodłowa.
      </p>
      <p>Uzasadnij, że przejazd tego samochodu z Jodłowa do Dębiny trwał dłużej niż godzinę. Zapisz obliczenia.</p>
    </div>
  );
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const ua = userAnswer || {};
  const set = (field) => (val) => onChange?.({ ...ua, [field]: val });
  return (
    <div style={{ display: "grid", gap: 14, textAlign: "left" }}>
      <div style={{ fontSize: 12.5, opacity: 0.78, lineHeight: 1.45, borderLeft: "3px solid var(--color-accent)", paddingLeft: 8 }}>
        <strong>{"Wyniki po\u015brednie s\u0105 opcjonalne."}</strong>{" "}
        {"Wpisz je, je\u015bli chcesz dosta\u0107 punkty cz\u0105stkowe. Poprawny czas przejazdu z jednostk\u0105 daje pe\u0142ne 3 punkty."}
      </div>
      <div>
        <div style={{ marginBottom: 4 }}>Prędkość samochodu (km/h):</div>
        <EquationInput
          value={ua.speed || ""}
          onChange={set("speed")}
          disabled={disabled}
          placeholder={`np. ${data.speed}`}
          basis="360px"
        />
      </div>
      <div>
        <div style={{ marginBottom: 4 }}>Długość drogi z Jodłowa do Dębiny (km):</div>
        <EquationInput
          value={ua.restDistance || ""}
          onChange={set("restDistance")}
          disabled={disabled}
          placeholder={`np. ${data.restDistance}`}
          basis="360px"
        />
      </div>
      <div>
        <div style={{ marginBottom: 4 }}>Czas przejazdu z Jodłowa do Dębiny (min):</div>
        <FractionInput
          value={ua.minutes || ""}
          onChange={set("minutes")}
          disabled={disabled}
          label=""
          suffixLatex="\\ \\text{min}"
          placeholder={`np. ${numberLatex(data.restMinutes).replace("{,}", ",")}`}
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer = {}) {
  const speedOk = hasNum(userAnswer.speed, data.speed);
  const restDistOk = hasNum(userAnswer.restDistance, data.restDistance);
  const resultOk = hasNum(userAnswer.minutes, data.restMinutes);
  // Wynik-first: poprawny czas (> 60 min) => komplet; bez wyniku — etapy częściowo.
  return scoreStaged({
    resultOk,
    milestones: [speedOk, restDistOk],
    maxScore: data.maxPoints,
    parts: {
      "prędkość": speedOk,
      "droga J→D": restDistOk,
      "czas przejazdu": resultOk,
    },
  });
}

export function renderAnswer({ data }) {
  const firstHoursLatex = minutesToHoursLatex(data.firstMinutes);
  const restMinutesLatex = numberLatex(data.restMinutes);
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>{"Przyk\u0142adowa poprawna odpowied\u017a:"}</div>
      <BlockMath math={`${data.firstMinutes}\\ \\ \\text{min}=${firstHoursLatex}\\ \\ \\text{h}`} />
      <BlockMath math={`v=\\frac{${data.firstDistance}}{${firstHoursLatex}}=${data.speed}\\ \\frac{\\ \\text{km}}{\\ \\text{h}}`} />
      <BlockMath math={`${data.totalDistance}-${data.firstDistance}=${data.restDistance}\\ \\ \\text{km}`} />
      <BlockMath math={`${data.restDistance}>${data.speed}`} />
      <div>{"Odpowied\u017a: przejazd trwa\u0142 d\u0142u\u017cej ni\u017c godzin\u0119; dok\u0142adnie "}<InlineMath math={`${restMinutesLatex}\\ \\ \\text{min}`} />{"."}</div>
    </div>
  );
}

export function renderUserAnswer(userAnswer = {}) {
  const minutes = rawStr(userAnswer.minutes);
  const speed = rawStr(userAnswer.speed);
  const rest = rawStr(userAnswer.restDistance);
  if (!minutes && !speed && !rest) return <i>(brak odpowiedzi)</i>;
  const speedLatex = lastNumberLatex(speed);
  const restLatex = lastNumberLatex(rest);
  const minutesLatex = lastNumberLatex(minutes);
  return (
    <div style={{ textAlign: "left", display: "grid", gap: 4 }}>
      <div>{"Pr\u0119dko\u015b\u0107: "}<InlineMath math={`${speedLatex || "\\ \\text{brak}"}\\frac{\text{km}}{\text{h}}`} /></div>
      <div>{"Droga J\u2192D: "}<InlineMath math={`${restLatex || "\\ \\text{brak}"}\\ \\text{km}`} /></div>
      <div>{"Czas przejazdu: "}<InlineMath math={`${minutesLatex || "\\ \\text{brak}"}\\ \\text{min}`} /></div>
    </div>
  );
}
export function renderExplanation({ data }) {
  const firstHoursLatex = minutesToHoursLatex(data.firstMinutes);
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p><strong>Krok 1.</strong> Obliczamy prędkość na trasie z Polanki do Jodłowa.</p>
      <BlockMath math={`${data.firstMinutes}\\ \\ \\text{min}=${firstHoursLatex}\\ \\ \\text{h}`} />
      <BlockMath math={`v=\\frac{${data.firstDistance}}{${firstHoursLatex}}=${data.speed}\\ \\frac{\\ \\text{km}}{\\ \\text{h}}`} />
      <p><strong>Krok 2.</strong> Droga z Jodłowa do Dębiny ma długość:</p>
      <BlockMath math={`${data.totalDistance}-${data.firstDistance}=${data.restDistance}\\ \\ \\text{km}`} />
      <p><strong>Krok 3.</strong> Samochód w godzinę przejechałby <InlineMath math={`${data.speed}\\ \\ \\text{km}`} />, a do pokonania ma <InlineMath math={`${data.restDistance}\\ \\ \\text{km}`} />.</p>
      <BlockMath math={`${data.restDistance}>${data.speed}`} />
      <p>Zatem przejazd trwał dłużej niż godzinę. Dokładnie: <InlineMath math={`${numberLatex(data.restMinutes)}\\ \\ \\text{min}`} />.</p>
    </div>
  );
}
